// EventNoteSystem.js
import React, { useState, useEffect, useRef } from "react";
import { 
  MessageSquare, 
  Edit2, 
  Save, 
  Clock, 
  X, 
  Bold, 
  Italic, 
  List, 
  CheckSquare,
  AlertCircle
} from "lucide-react";

import "./EventNoteSystem.css"


const NoteEditor = ({ note, onSave, onCancel }) => {
  const [noteText, setNoteText] = useState(note?.content || "");
  const [isImportant, setIsImportant] = useState(note?.isImportant || false);
  const editorRef = useRef(null);
  
  // 简单的富文本操作
  const formatText = (formatType) => {
    if (!editorRef.current) return;
    
    const selection = window.getSelection();
    if (!selection.rangeCount) return;
    
    const range = selection.getRangeAt(0);
    const selectedText = range.toString();
    
    if (!selectedText) return;
    
    let formattedText;
    switch(formatType) {
      case 'bold':
        formattedText = `<strong>${selectedText}</strong>`;
        break;
      case 'italic':
        formattedText = `<em>${selectedText}</em>`;
        break;
      case 'list':
        formattedText = `<ul><li>${selectedText}</li></ul>`;
        break;
      default:
        return;
    }
    
    // 创建一个新的HTML节点
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = formattedText;
    const newNode = tempDiv.firstChild;
    
    // 替换选中的文本
    range.deleteContents();
    range.insertNode(newNode);
    
    // 更新状态 - 重要的是使用当前HTML内容
    setNoteText(editorRef.current.innerHTML);
  };
  
  // 保存备注
  const handleSave = () => {
    const noteData = {
      id: note?.id || Date.now(),
      content: editorRef.current.innerHTML,
      timestamp: new Date().toISOString(),
      isImportant
    };
    onSave(noteData);
  };
  
  useEffect(() => {
    // 设置初始内容
    if (editorRef.current && note?.content) {
      editorRef.current.innerHTML = note.content;
    }
    
    // 确保正确的文本方向
    if (editorRef.current) {
      // 明确设置文本方向属性
      editorRef.current.setAttribute('dir', 'ltr');
      editorRef.current.style.direction = 'ltr';
      editorRef.current.style.textAlign = 'left';
    }
  }, [note]);
  
  // 以保持文本方向的方式处理输入更改
  const handleInput = (e) => {
    const content = e.currentTarget.innerHTML;
    setNoteText(content);
    // 每次编辑后重新确保方向
    e.currentTarget.setAttribute('dir', 'ltr');
    e.currentTarget.style.direction = 'ltr';
  };
  
  return (
    <div className="note-editor">
      <div className="note-editor-toolbar">
        <button className="format-btn" onClick={() => formatText('bold')} title="Bold">
          <Bold size={16} />
        </button>
        <button className="format-btn" onClick={() => formatText('italic')} title="Italic">
          <Italic size={16} />
        </button>
        <button className="format-btn" onClick={() => formatText('list')} title="List">
          <List size={16} />
        </button>
        {/* <div className="note-importance">
          <input 
            type="checkbox" 
            id="important-checkbox" 
            checked={isImportant}
            onChange={() => setIsImportant(!isImportant)}
          />
          <label htmlFor="important-checkbox" className="important-label">
            <AlertCircle size={16} /> Important
          </label>
        </div> */}
      </div>
      
      <div
        ref={editorRef}
        className="note-content-editor"
        contentEditable={true}
        onInput={handleInput}
        dir="ltr"
      />
      
      <div className="note-editor-footer">
       
        <button className="note-btn save" onClick={handleSave}>
          <Save size={16} />
          <span>Save Note</span>
        </button>
        <button className="note-btn cancel" onClick={onCancel}>
          <X size={16} />
          <span>Cancel</span>
        </button>
      </div>
    </div>
  );
};

// 备注预览组件
const NotePreview = ({ note, onEdit, onDelete }) => {
  // 格式化时间
  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return `${date.toLocaleDateString()} ${date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}`;
  };
  
  return (
    <div className={`note-preview ${note.isImportant ? 'important' : ''}`}>
      <div className="note-preview-header">
        <div className="note-timestamp">
          <Clock size={14} />
          <span>{formatDate(note.timestamp)}</span>
        </div>
        <div className="note-actions">
          <button className="note-action-btn" onClick={() => onEdit(note)}>
            <Edit2 size={14} />
          </button>
          <button className="note-action-btn" onClick={() => onDelete(note.id)}>
            <X size={14} />
          </button>
        </div>
      </div>
      <div 
        className="note-preview-content"
        dangerouslySetInnerHTML={{ __html: note.content }}
      />
    </div>
  );
};

// 备注系统主组件
export const EventNoteSystem = ({ 
  eventId, 
  onNotesChange, 
  initialNotes = [],
  onAddToOtherArea,
  hideSaveButton = false,
  hideNoteToggleBtn = false
}) => {
  const [notes, setNotes] = useState(initialNotes);
  const [isEditing, setIsEditing] = useState(false);
  const [currentNote, setCurrentNote] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);
  
  // 保存备注
  const handleSaveNote = (noteData) => {
    let updatedNotes;
    
    if (currentNote) {
      // 更新现有备注
      updatedNotes = notes.map(note => 
        note.id === noteData.id ? { ...note, ...noteData } : note
      );
    } else {
      // 添加新备注
      updatedNotes = [...notes, noteData];
    }
    
    setNotes(updatedNotes);
    setIsEditing(false);
    setCurrentNote(null);
    
    // 通知父组件备注已更改
    if (onNotesChange) {
      onNotesChange(updatedNotes);
    }
  };
  
  // 编辑备注
  const handleEditNote = (note) => {
    setCurrentNote(note);
    setIsEditing(true);
  };
  
  // 删除备注
  const handleDeleteNote = (noteId) => {
    const updatedNotes = notes.filter(note => note.id !== noteId);
    setNotes(updatedNotes);
    
    // 通知父组件备注已更改
    if (onNotesChange) {
      onNotesChange(updatedNotes);
    }
  };
  
  // 获取最新备注时间
  const getLatestNoteTime = () => {
    if (notes.length === 0) return null;
    
    const sortedNotes = [...notes].sort((a, b) => 
      new Date(b.timestamp) - new Date(a.timestamp)
    );
    
    const latestDate = new Date(sortedNotes[0].timestamp);
    return latestDate.toLocaleDateString();
  };

  // 将事件及备注发送到其他区域
  const handleAddToOtherArea = () => {
    if (onAddToOtherArea) {
      onAddToOtherArea({
        eventId,
        notes
      });
    }
  };
  
  return (
    <div className="event-note-system">
      <div className="note-header">
        {!hideNoteToggleBtn && (
          <button 
            className="note-toggle-btn"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            <MessageSquare size={16} />
            <span className="note-count">{notes.length}</span>
            {notes.length > 0 && (
              <span className="note-latest-time">Last: {getLatestNoteTime()}</span>
            )}
          </button>
        )}
        {!hideSaveButton && (
          <button 
            className="send-to-area-btn"
            onClick={handleAddToOtherArea}
            title="Send to another area"
          >
            <span>save to workspace</span>
          </button>
        )}
      </div>
      
      {isExpanded && (
        <div className="notes-container">
          {isEditing ? (
            <NoteEditor 
              note={currentNote}
              onSave={handleSaveNote}
              onCancel={() => {
                setIsEditing(false);
                setCurrentNote(null);
              }}
            />
          ) : (
            <>
              <div className="notes-list">
                {notes.length > 0 ? (
                  notes.map(note => (
                    <NotePreview 
                      key={note.id}
                      note={note}
                      onEdit={handleEditNote}
                      onDelete={handleDeleteNote}
                    />
                  ))
                ) : (
                  <div className="no-notes">No notes yet</div>
                )}
              </div>
              
              <div className="note-buttons-container">
                <button 
                  className="add-note-btn"
                  onClick={() => {
                    setCurrentNote(null);
                    setIsEditing(true);
                  }}
                >
                  <Edit2 size={16} />
                  <span>Add Note</span>
                </button>
                <button 
                  className="cancel-btn"
                  onClick={() => setIsExpanded(false)}
                >
                  <X size={16} />
                  <span>Cancel</span>
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default EventNoteSystem;